import { Injectable } from '@angular/core';

import { BehaviorSubject, merge } from 'rxjs';
import { map, startWith, delay } from 'rxjs/operators';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { AppConfigService, AppConfigProperty } from '@library/app.config';

import { ShipmentService } from './shipment.service';

import { TypeComponent } from './type/type.component';
import { ShipperBillingPartyComponent } from './billing-party/shipper-billing-party.component';
import { ConsigneeBillingPartyComponent } from './billing-party/consignee-billing-party.component';
import { DatesComponent } from './dates/dates.component';
import { CalculatorComponent } from './calculator';
import { ContainersComponent } from './containers/containers.component';
import { ReferencesComponent } from './references/references.component';
import { TradeshowComponent } from './tradeshow/tradeshow.component';
import { RisksComponent } from './risks/risks.component';
import { DescriptionComponent } from './description/description.component';
import { CustomsComponent } from './customs/customs.component';
import { AttachmentsComponent } from './attachments/attachments.component';

@UntilDestroy()
@Injectable({
   providedIn: 'root',
})
export class ShipmentComponentsService {

   shipmentComponents$ = new BehaviorSubject<AppConfigProperty[]>( [] );

   shipmentComponents: AppConfigProperty = [
      { id: 'type', component: TypeComponent },
      { id: 'shipper', component: ShipperBillingPartyComponent },
      { id: 'consignee', component: ConsigneeBillingPartyComponent },
      { id: 'dates', component: DatesComponent },
      { id: 'calculator', component: CalculatorComponent, displayFn: () => this.shipment.mode !== 'ocean' },
      { id: 'containers', component: ContainersComponent, displayFn: () => this.shipment.mode === 'ocean' },
      { id: 'references', component: ReferencesComponent },
      { id: 'tradeshow', component: TradeshowComponent },
      { id: 'risks', component: RisksComponent },
      { id: 'description', component: DescriptionComponent },
      { id: 'customs', component: CustomsComponent, displayFn: () => this.shipment.shipType === 'international' },
      { id: 'attachments', component: AttachmentsComponent },
   ];

   constructor(
      private shipment: ShipmentService,
      private appConfig: AppConfigService,
   ) { }

   load() {
      merge(
         this.appConfig.configLoaded$,
         this.shipment.shipmentForm$,
      ).pipe(
         startWith( this.appConfig.config ),
         delay( 1 ),
         map( _ => this.appConfig.config.booking ),
         map( configs => {
            const defaultConfig = configs.filter( c => c.id === 'default' )[0];

            return configs.map( c => ({ ...defaultConfig, ...c }));
         }),
         map( configs =>
            this.shipmentComponents.reduce(( a, c ) => {
               const config = configs.filter( config => config.id === c.id )[0] ?? {};

               a.push({ ...config, ...c });

               return a;
            }, [])
         ),
         map( components => components.filter( item => item.displayFn ? item.displayFn() : true )),
         map( components => components.filter( item => item?.display ?? true )),
         map( components => components.sort(( a, b ) => ( a?.order ?? 0 ) - ( b?.order ?? 0 ))),
         untilDestroyed( this ),
      ).subscribe( components => this.shipmentComponents$.next( components ));
   }
}
